import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Mail,
  Paperclip,
  Users,
} from "lucide-react";
import { useState } from "react";
import { Skeleton } from "../../ui/skeleton";

import type {
  Email,
  EmailRecipient,
  EmailThread,
} from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardHeader,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardSubtitle,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Email, EmailRecipient, EmailThread };

type EmailThreadCardProps = EmailThread &
  Omit<BaseEntityCardProps, "onMentionClick"> & {
    onMentionClick?: (messageId?: string) => void;
  };

const decodeHtmlEntities = (text: string) => {
  const textarea = document.createElement("textarea");
  textarea.innerHTML = text;
  return textarea.value;
};

export function EmailThreadCardAvatar({
  className,
  style,
}: Partial<EmailThreadCardProps>) {
  return (
    <Avatar className={cn("h-10 w-10 shrink-0", className)} style={style}>
      <AvatarFallback>
        <Users className="h-5 w-5 text-muted-foreground" />
      </AvatarFallback>
    </Avatar>
  );
}

export function EmailThreadCard({
  platform,
  subject,
  messages,
  message_count,
  latest_timestamp,
  participants,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: EmailThreadCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Get latest message for preview
  const latestMessage = messages[messages.length - 1] || messages[0];

  // Check if any message has attachments
  const hasAttachments = messages.some((msg) => msg.has_attachments);

  // Get display participants (first 1-2 for Gmail-style display)
  const displayParticipants = participants.slice(0, 2);

  const handleCardClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleToggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  const handleWebLinkClick = (e: React.MouseEvent, webLink?: string) => {
    e.stopPropagation();
    if (webLink) {
      window.open(webLink, "_blank");
    }
  };

  const handleMessageClick = (e: React.MouseEvent, messageId: string) => {
    e.stopPropagation();
    if (onMentionClick) {
      onMentionClick(messageId);
    }
  };

  return (
    <BaseEntityCard
      onClick={handleCardClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <EmailThreadCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>
            {displayParticipants.map((p, i) => {
              const displayName = p.name || p.address;
              const truncatedName =
                displayName.length > 20
                  ? `${displayName.slice(0, 18)}..`
                  : displayName;

              return (
                <span key={p.address}>
                  {truncatedName}
                  {i < displayParticipants.length - 1 && ", "}
                </span>
              );
            })}
            {message_count > 1 && (
              <span className="text-muted-foreground ml-1">
                {message_count}
              </span>
            )}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {latest_timestamp &&
                formatDistanceToNow(new Date(latest_timestamp), {
                  addSuffix: true,
                })}
            </span>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={() => onMentionClick?.()} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          <BaseEntityCardSubtitle className="flex items-center gap-1.5">
            <span className="font-medium line-clamp-1">{subject}</span>
            {hasAttachments && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Has attachments</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </BaseEntityCardSubtitle>

          {latestMessage && (
            <BaseEntityCardDescription className="line-clamp-1">
              {latestMessage.body_preview &&
                decodeHtmlEntities(latestMessage.body_preview)}
            </BaseEntityCardDescription>
          )}

          {/* Expand/Collapse button for threads with multiple messages */}
          {message_count > 1 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 mt-1 text-xs hover:bg-accent"
              onClick={handleToggleExpand}
            >
              {isExpanded ? (
                <>
                  <ChevronDown className="h-3.5 w-3.5 mr-1" />
                  Hide all {message_count}{" "}
                  {message_count === 1 ? "message" : "messages"}
                </>
              ) : (
                <>
                  <ChevronRight className="h-3.5 w-3.5 mr-1" />
                  Show all {message_count}{" "}
                  {message_count === 1 ? "message" : "messages"}
                </>
              )}
            </Button>
          )}

          {/* Expanded message list */}
          {isExpanded && (
            <div className="mt-2 space-y-1 border-l-2 border-muted pl-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className="flex items-center gap-2 text-xs py-1 -mx-1 px-1 rounded cursor-pointer hover:bg-accent/50 transition-colors"
                  onClick={(e) => handleMessageClick(e, message.id)}
                >
                  <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
                  <span className="font-medium shrink-0">
                    {message.sender_name || message.sender_address}
                  </span>
                  <span className="text-muted-foreground shrink-0">
                    {formatDistanceToNow(new Date(message.timestamp), {
                      addSuffix: true,
                    })}
                  </span>
                  <span className="text-muted-foreground truncate flex-1">
                    {message.body_preview &&
                      decodeHtmlEntities(message.body_preview)}
                  </span>
                  {message.web_link && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-5 w-5 shrink-0"
                            onClick={(e) =>
                              handleWebLinkClick(e, message.web_link)
                            }
                          >
                            <ExternalLink className="h-3 w-3" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>View on the web</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              ))}
            </div>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}

export function EmailThreadCardSkeleton({
  className,
  style,
}: {
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <BaseEntityCard className={cn("h-[90px]", className)} style={style}>
      <Skeleton className="h-10 w-10 rounded-full" />
      <BaseEntityCardContent>
        <Skeleton className="h-4 w-1/3 mt-1.5 mb-1.5" />
        <Skeleton className="h-4 w-3/4 mb-1.5" />
        <Skeleton className="h-3 w-1/2" />
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
